import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { opsApi } from '../../services/api';
import { loadGoogleIdentity, onGoogleCredential } from '../../services/googleIdentity';
import { useToast } from '../../hooks/useToast';
import { KeyConfirmation, ProviderKey, ProviderKeys } from '../../types';
import { errorMessage } from '../../utils/errors';
import { formatDateTime } from '../../utils/format';

type Pending = { key: ProviderKey; action: 'set' | 'clear' };

/** Pulls the first message out of a `{field: [message]}` answer, which is how this API reports a refused change. */
function refusal(e: unknown, fallback: string): string {
  const data = (e as { response?: { data?: Record<string, unknown> } })?.response?.data;
  for (const field of ['confirm', 'value', 'detail']) {
    const value = data?.[field];
    if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
    if (typeof value === 'string') return value;
  }
  return errorMessage(e, fallback);
}

/**
 * The Keys section of the staff area: the keys JobFlo uses for outside
 * services. Write-only by design: this page learns whether a key is set and
 * its last four characters, never the key. Each change asks the owner to
 * prove who they are again, is checked with the service, logged and announced.
 */
const KeysPage: React.FC = () => {
  const { show, toast } = useToast();
  const [data, setData] = useState<ProviderKeys | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);
  const [value, setValue] = useState('');
  const [password, setPassword] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const googleSlot = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await opsApi.keys());
      setError(null);
    } catch (e) {
      if ((e as { response?: { status?: number } })?.response?.status === 403) setForbidden(true);
      else setError(errorMessage(e, 'Could not load the keys. Please try again.'));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const close = () => {
    setPending(null);
    setValue('');
    setPassword('');
    setProblem(null);
  };

  const submit = useCallback(
    async (confirmation: KeyConfirmation) => {
      if (!pending) return;
      if (pending.action === 'set' && !value.trim()) {
        setProblem('Paste the new key first.');
        return;
      }
      setSaving(true);
      setProblem(null);
      try {
        if (pending.action === 'set') await opsApi.setKey(pending.key.name, value.trim(), confirmation);
        else await opsApi.clearKey(pending.key.name, confirmation);
        show(pending.action === 'set' ? `${pending.key.name} saved. It is in use within five minutes.` : `${pending.key.name} cleared.`);
        setPending(null);
        setValue('');
        setPassword('');
        await load();
      } catch (e) {
        setProblem(refusal(e, 'The change could not be saved.'));
      } finally {
        setSaving(false);
      }
    },
    [load, pending, show, value]
  );

  // "Confirm with Google": the same button as on the sign-in page; its answer confirms the change.
  const submitRef = useRef(submit);
  submitRef.current = submit;
  useEffect(() => {
    if (!pending) return undefined;
    const release = onGoogleCredential((credential) => submitRef.current({ google_credential: credential }));
    let cancelled = false;
    loadGoogleIdentity()
      .then((googleId) => {
        const slot = googleSlot.current;
        if (cancelled || !slot) return;
        slot.textContent = '';
        googleId.renderButton(slot, { theme: 'outline', size: 'large', text: 'continue_with', width: 280 });
      })
      .catch(() => {
        // No Google button (blocked or offline): the password box still works.
      });
    return () => {
      cancelled = true;
      release();
    };
  }, [pending]);

  if (forbidden) {
    return <Alert severity="info">Only the owner of this JobFlo installation can see or change its keys.</Alert>;
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      )}
      {!data ? (
        !error && <Skeleton variant="rounded" height={260} />
      ) : !data.available ? (
        <Alert severity="info">This server has no keys secret, so there is nothing to manage here. In production the keys live in AWS Secrets Manager.</Alert>
      ) : (
        <Stack spacing={2}>
          <Paper variant="outlined" component="section" aria-labelledby="keys-title" sx={{ p: 2 }}>
            <Typography id="keys-title" variant="h6" component="h2" sx={{ fontWeight: 700 }}>
              Keys for outside services
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Stored in AWS Secrets Manager (jobflo/prod/keys) and read by the app every five minutes. This page can replace
              or clear a key but never shows one. Each change asks you to confirm it is you, is checked with the service,
              recorded below and emailed to the alert address.
            </Typography>
            <TableContainer>
              <Table size="small" aria-labelledby="keys-title" sx={{ minWidth: 640 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Key</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Last changed here</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.keys.map((key) => (
                    <TableRow key={key.name}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {key.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {key.label}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {key.set ? (
                          <Chip size="small" color="success" variant="outlined" label={key.last4 ? `Set, ending ${key.last4}` : 'Set'} />
                        ) : (
                          <Chip size="small" variant="outlined" label="Not set" />
                        )}
                      </TableCell>
                      <TableCell>{key.changed_at ? `${formatDateTime(key.changed_at)}${key.changed_by ? ` by ${key.changed_by}` : ''}` : 'Never'}</TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => setPending({ key, action: 'set' })}>
                          {key.set ? 'Replace' : 'Set'}
                        </Button>
                        {key.set && (
                          <Button size="small" color="inherit" onClick={() => setPending({ key, action: 'clear' })}>
                            Clear
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper variant="outlined" component="section" aria-labelledby="key-changes-title" sx={{ p: 2 }}>
            <Typography id="key-changes-title" variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
              Recent changes
            </Typography>
            {data.changes.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No key has been changed from this page. Changes made in the AWS console do not appear here.
              </Typography>
            ) : (
              <Stack spacing={0.5}>
                {data.changes.map((change, index) => (
                  <Typography key={index} variant="body2">
                    {formatDateTime(change.at)}: {change.name} {change.action} by {change.by || 'a removed account'}
                  </Typography>
                ))}
              </Stack>
            )}
          </Paper>
        </Stack>
      )}

      <Dialog open={pending !== null} onClose={saving ? undefined : close} fullWidth maxWidth="xs" aria-labelledby="key-dialog-title">
        <DialogTitle id="key-dialog-title">{pending?.action === 'clear' ? `Clear ${pending.key.name}?` : `${pending?.key.set ? 'Replace' : 'Set'} ${pending?.key.name ?? ''}`}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {problem && <Alert severity="error">{problem}</Alert>}
            {pending?.action === 'clear' ? (
              <Typography variant="body2">Whatever uses this key stops working until a new one is set.</Typography>
            ) : (
              <TextField
                label="New key"
                type="password"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                autoComplete="off"
                autoFocus
                helperText="It is checked with the service before it is stored, and never shown again."
              />
            )}
            <Divider />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Confirm it is you
            </Typography>
            <Box ref={googleSlot} sx={{ minHeight: 44, ...(saving && { pointerEvents: 'none', opacity: 0.6 }) }} />
            <TextField
              label="Or your JobFlo password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && password) submit({ password });
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" color={pending?.action === 'clear' ? 'error' : 'primary'} disabled={saving || !password} onClick={() => submit({ password })}>
            {saving ? 'Checking...' : pending?.action === 'clear' ? 'Clear it' : 'Check and save'}
          </Button>
        </DialogActions>
      </Dialog>
      {toast}
    </Box>
  );
};

export default KeysPage;
